export const MEETING_RECORDER_GUIDE = {
  id: "meeting-recorder",
  title: "Meeting Recorder Manual",
  description:
    "Complete guide for the desktop meeting recorder app — installation, setup, and usage.",
  icon: "Monitor",
  version: "0.1.0",
  lastUpdated: "2026-02-23",
  requires: [],
  sections: [
    /* ---------------------------------------------------------- */
    /*  What It Does                                              */
    /* ---------------------------------------------------------- */
    {
      id: "what-it-does",
      title: "What It Does",
      content: [
        {
          type: "paragraph",
          text: "The Insights Meeting Recorder is a lightweight desktop app that runs in your system tray and:",
        },
        {
          type: "steps",
          items: [
            "**Detects** when you join a Zoom, Microsoft Teams, or WhatsApp call",
            "**Records** your screen and audio using FFmpeg",
            "**Uploads** the recording to the Insights Platform automatically",
          ],
        },
        {
          type: "paragraph",
          text: "You can also start and stop recordings manually at any time.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Prerequisites                                             */
    /* ---------------------------------------------------------- */
    {
      id: "prerequisites",
      title: "Prerequisites",
      content: [
        {
          type: "paragraph",
          text: "Before using the recorder, you need:",
        },
        {
          type: "table",
          headers: ["Requirement", "Details"],
          rows: [
            ["**Operating system**", "Windows 10+ or macOS 10.15+"],
            ["**FFmpeg**", "Must be installed separately (see below)"],
            ["**Insights access**", "Your Insights Platform URL"],
          ],
        },
        {
          type: "heading",
          text: "Installing FFmpeg",
        },
        {
          type: "paragraph",
          text: "The recorder uses FFmpeg to capture your screen and audio. It is not bundled with the installer.",
        },
        {
          type: "paragraph",
          text: "**Windows:**",
        },
        {
          type: "steps",
          items: [
            "Download from https://ffmpeg.org/download.html (choose a Windows build)",
            "Extract the zip to a permanent location, e.g. `C:\\ffmpeg`",
            "Note the path to the executable: `C:\\ffmpeg\\bin\\ffmpeg.exe`",
          ],
        },
        {
          type: "paragraph",
          text: "**macOS:**",
        },
        {
          type: "steps",
          items: [
            "Install via Homebrew: `brew install ffmpeg`",
            "The path is typically `/usr/local/bin/ffmpeg` or `/opt/homebrew/bin/ffmpeg`",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Installation                                              */
    /* ---------------------------------------------------------- */
    {
      id: "installation",
      title: "Installation",
      content: [
        {
          type: "heading",
          text: "Windows",
        },
        {
          type: "steps",
          items: [
            "Go to the **Downloads** page in Insights and click **Download** on the Windows card",
            "Run the installer — Windows SmartScreen may show a warning because the app is not code-signed. Click **More info**, then **Run anyway**",
            "Choose your installation directory and complete setup",
            "Launch the app from the Start menu",
          ],
        },
        {
          type: "heading",
          text: "macOS",
        },
        {
          type: "steps",
          items: [
            "Go to the **Downloads** page in Insights and click **Download** on the macOS card",
            "Open the DMG and drag **Insights Meeting Recorder** to your Applications folder",
            "On first launch, macOS Gatekeeper will block the app. Open **System Settings > Privacy & Security**, scroll down and click **Open Anyway** next to the blocked app message",
            "Grant **Screen Recording** and **Microphone** permissions when prompted (System Settings > Privacy & Security)",
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  First-Time Setup                                          */
    /* ---------------------------------------------------------- */
    {
      id: "first-time-setup",
      title: "First-Time Setup",
      content: [
        {
          type: "paragraph",
          text: "When you open the app for the first time, configure two settings:",
        },
        {
          type: "heading",
          text: "1. Insights URL",
        },
        {
          type: "paragraph",
          text: "Enter your Insights Platform URL in the **Insights URL** field. This is the base URL of your Insights instance, for example:",
        },
        {
          type: "code",
          text: "https://fgf-crm.azurestaticapps.net",
        },
        {
          type: "heading",
          text: "2. FFmpeg Path",
        },
        {
          type: "paragraph",
          text: "Enter the full path to your FFmpeg executable in the **FFmpeg Path** field:",
        },
        {
          type: "steps",
          items: [
            "**Windows example:** `C:\\ffmpeg\\bin\\ffmpeg.exe`",
            "**macOS example:** `/usr/local/bin/ffmpeg`",
            "If FFmpeg is on your system PATH, you can simply enter `ffmpeg`",
          ],
        },
        {
          type: "paragraph",
          text: 'Click **Save Settings** when done. You should see "Settings saved" in the activity log.',
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Using the Recorder                                        */
    /* ---------------------------------------------------------- */
    {
      id: "using-the-recorder",
      title: "Using the Recorder",
      content: [
        {
          type: "heading",
          text: "The Main Window",
        },
        {
          type: "paragraph",
          text: "The app window has four sections:",
        },
        {
          type: "code",
          text: "+----------------------------------+\n|  Meeting Recorder                |\n|  Insights Platform               |\n+----------------------------------+\n|  Status: Idle — Monitoring...    |\n+----------------------------------+\n|  [Start Recording] [Stop]        |\n+----------------------------------+\n|  Settings                        |\n|  Insights URL: [____________]   |\n|  FFmpeg:  [____________]        |\n|  [Save Settings]                 |\n+----------------------------------+\n|  Activity Log                    |\n|  10:32:15 — Loaded settings      |\n+----------------------------------+",
        },
        {
          type: "heading",
          text: "Status Indicators",
        },
        {
          type: "table",
          headers: ["Status", "Meaning"],
          rows: [
            [
              "**Idle — Monitoring for meetings**",
              "App is watching for Zoom/Teams meetings",
            ],
            [
              "**Detected: Zoom — Weekly Standup**",
              "A meeting/call was found (shows platform and title)",
            ],
            ["**Recording...**", "Screen and audio are being captured"],
            [
              "**Uploading to Insights...**",
              "Recording is being sent to Insights",
            ],
            [
              "**Error — Check FFmpeg**",
              "FFmpeg failed to start (check your path)",
            ],
            [
              "**Upload failed**",
              "The upload to Insights did not succeed",
            ],
          ],
        },
        {
          type: "heading",
          text: "Automatic Recording",
        },
        {
          type: "paragraph",
          text: "When the app detects a Zoom, Teams, or WhatsApp call window, it will:",
        },
        {
          type: "steps",
          items: [
            'Show "Detected: [platform] — [title]" in the status area',
            "Automatically start recording (if auto-record is enabled in config)",
            "When the meeting window closes, stop recording",
            "Automatically upload the recording to Insights",
          ],
        },
        {
          type: "heading",
          text: "Manual Recording",
        },
        {
          type: "paragraph",
          text: "You can record at any time, even without a detected meeting:",
        },
        {
          type: "steps",
          items: [
            'Click **Start Recording** — the status turns red and shows "Recording..."',
            "Click **Stop Recording** when finished",
            "The recording uploads to Insights automatically",
          ],
        },
        {
          type: "heading",
          text: "System Tray",
        },
        {
          type: "paragraph",
          text: "When you close the window, the app keeps running in the system tray (notification area):",
        },
        {
          type: "steps",
          items: [
            "**Double-click** the tray icon to reopen the window",
            "**Right-click** the tray icon for a menu: **Show Window**, **Start Recording**, **Stop Recording**, **Quit**",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The app will continue monitoring for meetings and recording in the background as long as it is running in the tray.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  How Meetings Are Detected                                 */
    /* ---------------------------------------------------------- */
    {
      id: "how-meetings-are-detected",
      title: "How Meetings Are Detected",
      content: [
        {
          type: "paragraph",
          text: "The recorder checks every 5 seconds for running application windows that match known meeting patterns:",
        },
        {
          type: "table",
          headers: ["Platform", "Window title patterns"],
          rows: [
            ["**Zoom**", '"Zoom Meeting", "Zoom Webinar"'],
            [
              "**Microsoft Teams**",
              '"Microsoft Teams...Meeting", "Microsoft Teams...Call"',
            ],
            [
              "**WhatsApp**",
              '"WhatsApp" (Desktop app — voice and video calls)',
            ],
          ],
        },
        {
          type: "paragraph",
          text: "**How it works:**",
        },
        {
          type: "steps",
          items: [
            "**Windows:** Queries running processes via PowerShell",
            "**macOS:** Queries foreground processes via AppleScript",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "No screen access or audio access is needed for detection — it only reads window titles.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Recording Details                                         */
    /* ---------------------------------------------------------- */
    {
      id: "recording-details",
      title: "Recording Details",
      content: [
        {
          type: "table",
          headers: ["Setting", "Value"],
          rows: [
            ["**Video codec**", "H.264 (libx264)"],
            ["**Audio codec**", "AAC at 128 kbps"],
            ["**Frame rate**", "15 FPS"],
            ["**Quality**", "CRF 28 (moderate, compressed)"],
            ["**Preset**", "Ultrafast (minimal CPU impact)"],
            ["**Output format**", "MP4"],
            ["**Capture source**", "Full desktop + system audio"],
          ],
        },
        {
          type: "paragraph",
          text: "Recordings are saved temporarily in your system's temp directory as `meeting-XXXXXXXX.mp4` and uploaded to Azure Blob Storage automatically.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Upload Process                                            */
    /* ---------------------------------------------------------- */
    {
      id: "upload-process",
      title: "Upload Process",
      content: [
        {
          type: "paragraph",
          text: "After a recording stops, the app performs three steps:",
        },
        {
          type: "steps",
          items: [
            "**Requests an upload token** from Insights (`/api/meetings/upload`)",
            "**Uploads the MP4 file** directly to Azure Blob Storage using the token",
            "**Creates a meeting record** in the Insights database (`/api/meetings`)",
          ],
        },
        {
          type: "paragraph",
          text: "The meeting record includes:",
        },
        {
          type: "steps",
          items: [
            'Platform (Zoom, Teams, WhatsApp, or Manual)',
            'Meeting title (from the window title, or "Manual Recording")',
            "Recording date and time",
            "Duration and file size",
            "Link to the uploaded video",
          ],
        },
        {
          type: "paragraph",
          text: "You can view uploaded recordings on the **Videos** page in Insights.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Troubleshooting                                           */
    /* ---------------------------------------------------------- */
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "Error — Check FFmpeg",
              answer:
                "FFmpeg could not be found or failed to start.\n\n- Verify FFmpeg is installed: open a terminal and run `ffmpeg -version`\n- Check the path in Settings matches your FFmpeg installation\n- On Windows, use the full path including `.exe`: `C:\\ffmpeg\\bin\\ffmpeg.exe`",
            },
            {
              question: "Upload failed",
              answer:
                "The recording could not be sent to Insights.\n\n- Check your internet connection\n- Verify the Insights URL in Settings is correct and accessible\n- Check that Insights is online and the `/api/meetings` endpoints are available\n- Check the activity log for specific error details",
            },
            {
              question: "API base URL not configured",
              answer:
                "You haven't set the Insights URL yet.\n\n- Open the app window and enter your Insights URL in the Settings section\n- Click **Save Settings**",
            },
            {
              question: "No meetings are being detected",
              answer:
                "- Make sure the meeting app (Zoom, Teams, or WhatsApp Desktop) is running and you have joined a call\n- The recorder checks window titles — if the meeting window is minimized it should still be detected\n- Detection runs every 5 seconds, so wait a moment after joining\n- WhatsApp Web is not supported — you must use the WhatsApp Desktop app",
            },
            {
              question: "Windows SmartScreen blocks the installer",
              answer:
                "The installer is not code-signed. When you see the SmartScreen warning:\n\n1. Click **More info**\n2. Click **Run anyway**",
            },
            {
              question: "macOS Gatekeeper blocks the app",
              answer:
                "Because the app is not notarized, macOS Gatekeeper will block it on first launch.\n\n1. Try to open the app normally — it will be blocked\n2. Open **System Settings > Privacy & Security**\n3. Scroll down to find the blocked app message\n4. Click **Open Anyway**\n\nYou may also need to grant additional permissions:\n\n- **Screen Recording** — required for video capture\n- **Microphone** — required for audio capture\n- **Accessibility** — may be needed for meeting detection",
            },
            {
              question: "Recording has no audio",
              answer:
                '- **Windows:** FFmpeg expects a DirectShow audio device called `virtual-audio-capturer`. You may need to install a virtual audio capture driver, or the audio device name may differ on your system.\n- **macOS:** The AVFoundation audio device index may need adjustment. The default is "1:0" (screen index 1, audio index 0).',
            },
          ],
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Where Files Are Stored                                    */
    /* ---------------------------------------------------------- */
    {
      id: "where-files-are-stored",
      title: "Where Files Are Stored",
      content: [
        {
          type: "table",
          headers: ["What", "Location"],
          rows: [
            [
              "**App settings**",
              "Windows: `%APPDATA%\\insights-platform-meeting-recorder\\` | macOS: `~/Library/Application Support/insights-platform-meeting-recorder/`",
            ],
            [
              "**Temporary recordings**",
              "System temp directory (`%TEMP%` on Windows, `/tmp` on macOS)",
            ],
            [
              "**Uploaded recordings**",
              "Azure Blob Storage (accessible via the Insights Videos page)",
            ],
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "Temporary recording files are not automatically cleaned up. You can safely delete any `meeting-*.mp4` files from your temp directory.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Configuration Reference                                   */
    /* ---------------------------------------------------------- */
    {
      id: "configuration-reference",
      title: "Configuration Reference",
      content: [
        {
          type: "paragraph",
          text: "These settings are stored in the app's config file and can be edited via the Settings section in the app:",
        },
        {
          type: "table",
          headers: ["Setting", "Default", "Description"],
          rows: [
            ["`apiBaseUrl`", "*(empty)*", "Your Insights instance URL"],
            ["`ffmpegPath`", "*(empty)*", "Path to FFmpeg executable"],
            [
              "`autoRecord`",
              "`false`",
              "Automatically record when a meeting is detected",
            ],
            [
              "`detectionIntervalMs`",
              "`5000`",
              "How often to check for meetings (milliseconds)",
            ],
            [
              "`maxRecordingMinutes`",
              "`180`",
              "Maximum recording length (not yet enforced)",
            ],
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "The `autoRecord` and `detectionIntervalMs` settings are not yet exposed in the UI and can be edited manually in the config JSON file if needed.",
        },
      ],
    },

    /* ---------------------------------------------------------- */
    /*  Quitting the App                                          */
    /* ---------------------------------------------------------- */
    {
      id: "quitting-the-app",
      title: "Quitting the App",
      content: [
        {
          type: "callout",
          variant: "warning",
          text: "Closing the window does **not** quit the app — it minimizes to the system tray and keeps monitoring for meetings.",
        },
        {
          type: "paragraph",
          text: "To fully quit:",
        },
        {
          type: "steps",
          items: [
            "Right-click the tray icon and choose **Quit**",
            "Alternatively, use Task Manager (Windows) / Activity Monitor (macOS) to end the process",
          ],
        },
      ],
    },
  ],
};
